import { SyntheticEvent, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  Grid,
  Chip,
  Skeleton,
  Alert,
  Button,
  Tooltip,
  Tabs,
  Tab,
  Badge,
  IconButton,
} from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useGetInvestment } from '@/lib/api/investments';
import { formatCurrency, formatDate, formatPercentage } from '@/utils/misc';
import { getInvestmentCountryLabel } from '@/features/investments/countries';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { investmentTypeLabels } from '@/features/investments/constants';
import { InvestmentStatus } from '@inversor-hub/types';
import { investmentStatusLabelMap } from '@/features/investments/util';
import GroupIcon from '@mui/icons-material/Group';
import { useGetInvestmentChecklist } from '@/lib/api/investment-checklist';
import {
  OverviewTab,
  PerformanceTab,
  TransactionsTab,
  RentalHistoryTab,
  IssuesTab,
  FiscalTab,
  DocumentsTab,
  MediaTab,
  CoInvestorsTab,
} from '@/features/investments/tabs';
import { InvestmentAssistantDrawer } from '@/features/investments/InvestmentAssistantDrawer';
import UpgradeWallModal from '@/features/billing/components/UpgradeWallModal.tsx';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import { ANALYTICS_PLATFORM, trackProductEvent } from '@/lib/analytics/product-events';
import { useInvestorInvestmentAccess } from '@/features/billing/useInvestorInvestmentAccess.ts';
import { useGetInvestmentIssueStats } from '@/lib/api/investment-issues';
import { useMarkQuickGuideAction } from '@/lib/api/me';
import { useSnackbar } from 'notistack';

const statusLabels: Record<
  InvestmentStatus,
  { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }
> = {
  PURCHASING: { label: investmentStatusLabelMap['PURCHASING'], color: 'warning' },
  RENOVATING: { label: investmentStatusLabelMap['RENOVATING'], color: 'warning' },
  LISTED_FOR_RENT_OR_SALE: {
    label: investmentStatusLabelMap['LISTED_FOR_RENT_OR_SALE'],
    color: 'primary',
  },
  RENTED: { label: investmentStatusLabelMap['RENTED'], color: 'success' },
  SOLD: { label: investmentStatusLabelMap['SOLD'], color: 'success' },
};

const INVESTMENT_DETAIL_TABS = [
  'overview',
  'performance',
  'rental-history',
  'transactions',
  'issues',
  'fiscal',
  'documents',
  'media',
  'coinvestors',
] as const;

type TabValue = (typeof INVESTMENT_DETAIL_TABS)[number];

const InvestmentConceptInfo = ({ title }: { title: string }) => (
  <Tooltip title={title} arrow>
    <InfoOutlinedIcon
      fontSize="inherit"
      sx={{ ml: 0.5, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle' }}
    />
  </Tooltip>
);

const InvestmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const {
    user: currentUser,
    isLockedInvestment,
    isLoading: isLoadingAccess,
    upgradeWallInvestmentCount,
    upgradeWallPlansPath,
  } = useInvestorInvestmentAccess();
  const { data: investment, isLoading, isError } = useGetInvestment(id);
  const { data: checklist, isLoading: isLoadingChecklist } = useGetInvestmentChecklist(id);
  const { data: issueStats } = useGetInvestmentIssueStats(id);
  const { trackEvent } = useAnalytics();
  const { mutate: markQuickGuideAction } = useMarkQuickGuideAction();
  const { enqueueSnackbar } = useSnackbar();

  const requestedTab = searchParams.get('tab') as TabValue | null;
  const currentTab =
    requestedTab && INVESTMENT_DETAIL_TABS.includes(requestedTab) ? requestedTab : 'overview';
  const hasChecklist = !!checklist;
  const openIssuesCount = issueStats?.openCount ?? 0;

  const handleTabChange = (_event: SyntheticEvent, newValue: TabValue) => {
    setSearchParams(newValue === 'overview' ? {} : { tab: newValue }, { replace: true });
  };

  const status = useMemo(
    () =>
      investment
        ? statusLabels[investment.status]
        : { label: 'Desconocido', color: 'default' as const },
    [investment]
  );

  const type = useMemo(
    () => (investment ? investmentTypeLabels[investment.type] : 'Tipo desconocido'),
    [investment]
  );

  if (isLoading || isLoadingAccess) {
    return (
      <Box p={3}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  if (isLockedInvestment(id)) {
    return (
      <UpgradeWallModal
        open
        onClose={() => navigate('/inversiones')}
        onViewPlans={() => navigate(upgradeWallPlansPath)}
        investmentCount={upgradeWallInvestmentCount}
      />
    );
  }

  if (isError || !investment) {
    return (
      <Box p={3}>
        <Alert severity="error">No se pudo cargar la inversión. Intenta de nuevo más tarde.</Alert>
      </Box>
    );
  }

  const canViewCoInvestorsTab = currentUser?.role === 'psi' && investment.isCoInvestment;
  const isInvestmentOwner =
    currentUser?.role === 'investor' && investment.investorId === currentUser.id;
  const selectedTab: TabValue =
    currentTab === 'coinvestors' && !canViewCoInvestorsTab ? 'overview' : currentTab;

  const handleAssistantOpen = () => {
    if (currentUser) {
      trackProductEvent(trackEvent, 'ai_assistant_opened', {
        entry_point: 'investment',
        role: currentUser.role,
        platform: ANALYTICS_PLATFORM,
      });
    }

    markQuickGuideAction('investment_assistant');

    setIsAssistantOpen(true);
  };

  const handleCopyIdentificationCode = async () => {
    if (!investment.identificationCode) return;

    try {
      await navigator.clipboard.writeText(investment.identificationCode);
      enqueueSnackbar('Código copiado', { variant: 'success' });
    } catch (_error) {
      enqueueSnackbar('No se pudo copiar el código', { variant: 'error' });
    }
  };

  return (
    <Box p={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box display="flex" alignItems="center">
          <ArrowBackIcon
            sx={{ cursor: 'pointer', mr: 1 }}
            onClick={() => navigate('/inversiones')}
          />
          <Typography variant="h5" fontWeight={700}>
            Gestión de Inversión
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Tooltip
            title={
              hasChecklist
                ? 'Ver el itinerario de tareas de la inversión'
                : 'El itinerario aún no está disponible'
            }
          >
            <span>
              <Button
                variant="contained"
                startIcon={<ChecklistIcon />}
                onClick={() => navigate(`/inversiones/${id}/checklist`)}
                disabled={!hasChecklist || isLoadingChecklist}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Ver itinerario
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/inversiones/${id}/editar`)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Editar
          </Button>
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleAssistantOpen}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Asistente IA
          </Button>
        </Box>
      </Box>

      <Card elevation={2} sx={{ mb: 3 }}>
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }}>
          {investment.mainImageUrl ? (
            <CardMedia
              component="img"
              image={investment.mainImageUrl}
              alt={investment.address}
              sx={{ width: { xs: '100%', md: 300 }, height: 200, objectFit: 'cover' }}
            />
          ) : (
            <Box
              sx={{
                width: { xs: '100%', md: 300 },
                height: 200,
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography color="text.secondary">Sin imagen</Typography>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                {investment.alias || investment.address}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {investment.address}, {investment.city}
                {investment.country && investment.country !== 'ES'
                  ? `, ${getInvestmentCountryLabel(investment.country)}`
                  : ''}
              </Typography>
              <Box>
                <Chip label={status.label} color={status.color} size="small" sx={{ mr: 1 }} />
                <Chip label={type} variant="outlined" size="small" sx={{ mr: 1 }} />
                {investment.isCoInvestment && (
                  <Chip
                    icon={<GroupIcon sx={{ fontSize: 14 }} />}
                    label="Co-inversión"
                    size="small"
                    color="secondary"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>

            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Inversión Total
                  <InvestmentConceptInfo title="Importe total invertido en la operación, incluyendo compra y costes asociados registrados." />
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatCurrency(investment.capex.total)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Rentabilidad Bruta (prevista)
                  <InvestmentConceptInfo title="Previsión según la ficha: renta anual estimada dividida entre la inversión total, antes de descontar gastos recurrentes. La real está en la pestaña Rentabilidad real." />
                </Typography>
                <Typography variant="body1" fontWeight={600} color="success.main">
                  {formatPercentage(investment.grossReturn)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Rentabilidad Neta (prevista)
                  <InvestmentConceptInfo title="Previsión según la ficha, después de descontar los gastos recurrentes estimados. La real está en la pestaña Rentabilidad real." />
                </Typography>
                <Typography variant="body1" fontWeight={600} color="primary.main">
                  {formatPercentage(investment.netReturn)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Fecha de compra
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatDate(investment.purchaseDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Código de identificación
                  <InvestmentConceptInfo title="Código que puedes indicar al inquilino para que lo use como concepto del pago." />
                </Typography>
                {investment.identificationCode ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      sx={{
                        fontFamily: 'monospace',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {investment.identificationCode}
                    </Typography>
                    <Tooltip title="Copiar código">
                      <IconButton
                        size="small"
                        onClick={handleCopyIdentificationCode}
                        aria-label="Copiar código de identificación"
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigate(`/inversiones/${id}/editar`)}
                    sx={{ px: 0, minWidth: 0, textTransform: 'none' }}
                  >
                    Añadir código
                  </Button>
                )}
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Renta Mensual
                  <InvestmentConceptInfo title="Ingreso mensual previsto o registrado por el alquiler de la propiedad." />
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {investment.monthlyRent ? formatCurrency(investment.monthlyRent) : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Titularidad
                  <InvestmentConceptInfo title="Porcentaje de propiedad que corresponde a tu participación en esta inversión." />
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatPercentage(investment.ownershipPercentage)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Fecha alta
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatDate(investment.createdAt)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          marginBottom: 3,
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.95rem',
          },
        }}
      >
        <Tab label="Resumen" value="overview" />
        <Tab label="Rentabilidad real" value="performance" />
        <Tab label="Alquileres" value="rental-history" />
        <Tab label="Transacciones" value="transactions" />
        <Tab label="Fiscal" value="fiscal" />
        <Tab
          label={
            openIssuesCount > 0 ? (
              <Badge badgeContent={openIssuesCount} color="warning" max={99}>
                <Box component="span" sx={{ pr: 1.5 }}>
                  Incidencias
                </Box>
              </Badge>
            ) : (
              'Incidencias'
            )
          }
          value="issues"
        />
        <Tab label="Documentos" value="documents" />
        <Tab label="Multimedia" value="media" />
        {canViewCoInvestorsTab && <Tab label="Coinversores" value="coinvestors" />}
      </Tabs>

      <Box sx={{ minHeight: 400 }}>
        {selectedTab === 'overview' && <OverviewTab investment={investment} />}
        {selectedTab === 'performance' && (
          <PerformanceTab investmentId={id!} investment={investment} canEdit={isInvestmentOwner} />
        )}
        {selectedTab === 'rental-history' && (
          <RentalHistoryTab investmentId={id!} investmentType={investment.type} />
        )}
        {selectedTab === 'transactions' && (
          <TransactionsTab investmentId={id!} investmentType={investment.type} />
        )}
        {selectedTab === 'issues' && <IssuesTab investmentId={id!} canEdit={isInvestmentOwner} />}
        {selectedTab === 'fiscal' && <FiscalTab investmentId={id!} />}
        {selectedTab === 'documents' && (
          <DocumentsTab
            investmentId={id!}
            opportunityId={investment.opportunityId}
            canEdit={true}
          />
        )}
        {selectedTab === 'media' && (
          <MediaTab investmentId={id} opportunityId={investment.opportunityId} canEdit={true} />
        )}
        {selectedTab === 'coinvestors' && canViewCoInvestorsTab && (
          <CoInvestorsTab investment={investment} />
        )}
      </Box>

      <InvestmentAssistantDrawer
        open={isAssistantOpen}
        onClose={() => setIsAssistantOpen(false)}
        investmentId={id!}
        investmentAddress={`${investment.address}, ${investment.city}`}
      />
    </Box>
  );
};

export default InvestmentDetailPage;
